import { useAuth0 } from '@auth0/auth0-react';
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { urlJoin } from 'url-join-ts';

import { useAuth } from '../../auth/AuthProvider';
import { PaymentPublicClient } from '../../backend-client/payment-public-backend';
import { MessageLoading } from '../../components/Loading';
import Signout from '../../components/Signout';
import { useGenerateSigningUrlMutation } from '../../generated/graphql';
import getEnv from '../../utils/getEnv';
import Error from '../Error';

const CLASS_NAME = 'Terms m-auto text-lg container text-center';

const {
  REACT_APP_API_MAX_RETRIES,
  REACT_APP_API_BASE_DELAY,
  REACT_APP_API_MAX_DELAY,
  REACT_APP_PUBLIC_URL
} = getEnv();

// Validate URLs before assigning to window.location.href to prevent
// open-redirect / XSS via javascript:/data:/vbscript: schemes (Aikido SAST 159108667).
const isSafeRedirectUrl = (value: unknown): value is string => {
  if (typeof value !== 'string' || value.length === 0) {
    return false;
  }
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
};

interface TermsParams {
  // Route param value is an applicationGroupId under the current link scheme;
  // legacy T&C links pass a quoteId. The backend generateSigningURL accepts
  // either.
  id: string;
}

const Terms: React.FC = () => {
  const [reachMaxRetries, setReachMaxRetries] = useState(false);
  // Fire the signing mutation at most ONCE per mount. The effect below depends
  // on auth state (isEmailValid / id), which settles across several re-renders
  // for a session-authenticated broker; without this guard each re-render would
  // fire generateSigningURL again, and since every call mints a fresh DocuSign
  // envelope, the customer could sign one envelope while the owner record holds
  // a later one — breaking the callback's stored-vs-signed match.
  const hasRequestedSigningUrl = useRef(false);
  const queryParams = new URLSearchParams(location.search);
  // Legacy links pass ?quote=<quoteId>; current links pass the id in the path.
  let id = queryParams.get('quote') ?? undefined;

  const { id: tempId } = useParams<Partial<TermsParams>>();

  if (!id) {
    id = tempId;
  }

  const [
    generateSigningUrl,
    { error: signingUrlError }
  ] = useGenerateSigningUrlMutation();
  const {
    user: authUser,
    isLoading: isAuthLoading,
    isAuthenticated: isAuthAuthenticated
  } = useAuth();
  const {
    user: auth0User,
    error: auth0Error,
    isLoading: isAuth0Loading
  } = useAuth0();
  const user = isAuthAuthenticated ? authUser : auth0User;
  const { email_verified, email } = user || {};
  const isEmailValid = email_verified && !!email;

  // Canonicalize the link to /terms/<applicationGroupId>. Runs independent of
  // auth — a legacy /terms/<quoteId> link comes back anonymous until the URL is
  // the group id — and uses a full-page navigation so AuthProvider re-runs its
  // /me call against the canonical id.
  useEffect(() => {
    if (!id) {
      return;
    }
    let cancelled = false;

    new PaymentPublicClient()
      .getApplicationGroupId(id)
      .then((applicationGroupId) => {
        if (cancelled || applicationGroupId === id) {
          return;
        }
        window.location.replace(
          urlJoin(
            window.location.origin,
            REACT_APP_PUBLIC_URL,
            'terms',
            applicationGroupId
          )
        );
      })
      .catch(() => {
        // Couldn't resolve (e.g. a legacy quote not yet in a group) — leave the
        // URL as-is; generateSigningURL accepts the inbound id either way.
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (!isEmailValid) {
      return;
    }

    if (!id) {
      return;
    }

    // Guard against re-render re-fires: only start the signing flow once.
    if (hasRequestedSigningUrl.current) {
      return;
    }
    hasRequestedSigningUrl.current = true;

    const generateSigningUrlWithRetry = async (
      id: string,
      attempt: number,
      maxRetries: number,
      delay: number,
      maxDelay: number
    ) => {
      try {
        const signingUrl = await generateSigningUrl({
          variables: { quoteOrGroupId: id }
        });
        const ret = signingUrl.data?.generateSigningURL;
        if (!ret) {
          <Error message="Couldn't get the signing Url" />;
        }
        if (!isSafeRedirectUrl(ret)) {
          setReachMaxRetries(true);
          return;
        }
        window.location.href = ret;
      } catch (error) {
        attempt++;

        if (attempt >= maxRetries) {
          setReachMaxRetries(true);
          throw error;
        }

        // Calculate the next retry delay with exponential backoff and jitter
        const jitter = Math.random() * delay;
        const exponentialBackoff = Math.min(delay * 2, maxDelay);
        delay = Math.min(exponentialBackoff + jitter, maxDelay);

        // Wait for the calculated delay before retrying
        await new Promise((resolve) => setTimeout(resolve, delay));

        // Return the recursive call so a final-attempt rejection propagates
        // to the top-level catch instead of becoming unhandled.
        return generateSigningUrlWithRetry(
          id,
          attempt,
          maxRetries,
          delay,
          maxDelay
        );
      }
    };

    // By the time the user is authenticated, a legacy link has already been
    // redirected to its canonical /terms/<applicationGroupId>, so `id` here is
    // the group id. generateSigningURL still validates it server-side.
    generateSigningUrlWithRetry(
      id,
      0,
      Number(REACT_APP_API_MAX_RETRIES),
      Number(REACT_APP_API_BASE_DELAY),
      Number(REACT_APP_API_MAX_DELAY)
    ).catch(() => {
      // Final-attempt failure already surfaced via setReachMaxRetries +
      // signingUrlError; without this catch the rejection is unhandled.
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmailValid, id, generateSigningUrl]);

  if (signingUrlError && reachMaxRetries) {
    return <Error apolloError={signingUrlError} />;
  }

  if (auth0Error) {
    return (
      <div className={CLASS_NAME}>
        There is an Auth0 error: {auth0Error.message}
      </div>
    );
  }

  if (!isAuth0Loading && !isAuthLoading) {
    if (!user) {
      return (
        <div className={CLASS_NAME}>
          Cannot retrieve user profile from Auth0.
        </div>
      );
    }

    if (!isEmailValid) {
      return (
        <div className={`${CLASS_NAME} leading-loose`}>
          <div className="text-lg font-bold">Please keep this tab open.</div>
          We have sent the verification email. Please check your inbox and
          refresh this tab after completing email verification.
        </div>
      );
    }
  }

  return (
    <>
      <Signout />
      <MessageLoading
        headingMessage="Your patience is appreciated!"
        subHeadingMessage="We are creating the documents you'll need to review & sign"
      />
    </>
  );
};

export default Terms;
